import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  FootballManagerTaskState,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { namedOperations } from '@sorare/core/src/__generated__/operations';
import commonEssenceBig from '@sorare/core/src/assets/rewards/common-essence-big.png';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useClaimManagerTask } from '@sorare/gameplay/src/hooks/useClaimManagerTask';

import type { FirstWeekClaim_mlbManagerTask } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: 0 var(--triple-unit);
  max-width: 100%;
  flex: 1;
`;

const FeaturedRewardWrapped = styled(Horizontal).attrs({
  gap: 0,
  center: true,
})`
  flex: 1;

  &.isFeaturedRewardLocked {
    filter: brightness(0.5);
  }
`;
const FeaturedRewardImg = styled(ResponsiveImg)`
  max-width: 270px;
  max-height: min(34vh, 300px);
  height: 100%;
  object-fit: contain;
`;

type Props = {
  dailyClaimTasks: FirstWeekClaim_mlbManagerTask[];
  onClose: () => void;
};

export const FirstWeekClaim = ({ dailyClaimTasks, onClose }: Props) => {
  const [claim] = useClaimManagerTask();
  const { isIosApp } = useIsMobileApp();

  const featuredReward = dailyClaimTasks[0];

  const featureRewardImage = commonEssenceBig;

  const isFeaturedRewardLocked =
    !featuredReward ||
    featuredReward.aasmState !== FootballManagerTaskState.COMPLETED;

  const onClaim = async ({ managerTaskId }: { managerTaskId: string }) => {
    await claim({
      refetchQueries: [namedOperations.Query.MLBDailyClaimDialogQuery],
      variables: {
        sport: Sport.BASEBALL,
        input: {
          footballManagerTaskId: managerTaskId,
        },
      },
    });

    onClose();
  };

  return (
    <Root gap={3} center>
      <Vertical center>
        <DisplayS className="text-center">
          <FormattedMessage
            id="ClaimArenaTicketDialog.title"
            defaultMessage="Daily Claim"
          />
        </DisplayS>
        <LabelM as="span" color="var(--c-nd-600)">
          <FormattedMessage
            id="NBA.DailyClaim.dialog.subtitle"
            defaultMessage="Claim a new reward every 24 hours"
          />
        </LabelM>
      </Vertical>
      <FeaturedRewardWrapped
        className={classNames({
          isFeaturedRewardLocked,
        })}
      >
        {!!featuredReward && (
          <ButtonBase
            disabled={
              featuredReward.aasmState !== FootballManagerTaskState.COMPLETED
            }
            onClick={async () =>
              await onClaim({
                managerTaskId: featuredReward?.id,
              })
            }
          >
            <Vertical center gap={2}>
              <FeaturedRewardImg src={featureRewardImage} alt="" />
              {!!featuredReward?.rewardConfig.cardShards?.[0] && (
                <LabelL bold className="text-center whitespace-nowrap">
                  {`x${featuredReward?.rewardConfig.cardShards[0].config.quantity}`}
                </LabelL>
              )}
            </Vertical>
          </ButtonBase>
        )}
      </FeaturedRewardWrapped>
      {!!isFeaturedRewardLocked && !!featuredReward?.readyAt && (
        <Vertical center>
          <LabelM as="span" color="var(--c-nd-600)">
            <FormattedMessage
              id="MLB.DailyClaim.FirstWeekClaim.comeBackIn"
              defaultMessage="Come back in"
            />
          </LabelM>
          <Countdown date={featuredReward?.readyAt} />
        </Vertical>
      )}
      {!!featuredReward && (
        <>
          {isIosApp ? (
            <Button
              fullWidth
              size="large"
              color="secondary"
              disabled={isFeaturedRewardLocked}
              onClick={async () =>
                await onClaim({
                  managerTaskId: featuredReward?.id,
                })
              }
            >
              <FormattedMessage {...glossary.claimNow} />
            </Button>
          ) : (
            <Button
              fullWidth
              size="large"
              color="secondary"
              onClick={
                isFeaturedRewardLocked
                  ? () => onClose()
                  : async () =>
                      await onClaim({
                        managerTaskId: featuredReward?.id,
                      })
              }
            >
              {isFeaturedRewardLocked ? (
                <FormattedMessage {...glossary.continue} />
              ) : (
                <FormattedMessage {...glossary.claimNow} />
              )}
            </Button>
          )}
        </>
      )}
    </Root>
  );
};

FirstWeekClaim.fragments = {
  mlbManagerTask: gql`
    fragment FirstWeekClaim_mlbManagerTask on FootballManagerTask {
      id
      aasmState
      label
      readyAt
      rewardConfig {
        cardShards {
          id
          config {
            id
            quantity
            rarity
          }
        }
      }
    }
  ` as TypedDocumentNode<FirstWeekClaim_mlbManagerTask>,
};
